import { useState } from 'react'
import { robinhoodChain } from '@algoboo/chain/chains'
import { Icon } from '../icons/index.js'
import { shortAddress } from '../format/time.js'
import { cx } from './cx.js'

export const BLOCKSCOUT_URL = robinhoodChain.blockExplorers.default.url

export type ExplorerKind = 'address' | 'tx' | 'token' | 'block'

export function explorerUrl(kind: ExplorerKind, value: string, base: string = BLOCKSCOUT_URL): string {
  return `${base}/${kind}/${value}`
}

/**
 * An address, tx hash, token or block linked to Blockscout (Robinhood
 * Chain), shortened with the full value in the tooltip, plus copy.
 */
export function AddressLink({
  value,
  kind = 'address',
  label,
  copy = true,
  className,
  lead,
  tail,
}: {
  value: string
  kind?: ExplorerKind
  label?: string
  copy?: boolean
  className?: string
  lead?: number
  tail?: number
}) {
  const [copied, setCopied] = useState(false)
  const text = label ?? (kind === 'block' ? `#${value}` : shortAddress(value, lead ?? (kind === 'tx' ? 8 : 6), tail ?? 4))
  const noun = kind === 'tx' ? 'transaction' : kind
  return (
    <span className={cx('inline-flex items-center gap-1 font-mono text-[12.5px]', className)}>
      <a
        href={explorerUrl(kind, value)}
        target="_blank"
        rel="noreferrer noopener"
        title={`${value} · open ${noun} on Blockscout`}
        className="inline-flex items-center gap-0.5 rounded text-ink-2 underline decoration-line-strong decoration-dotted underline-offset-[3px] hover:text-accent hover:decoration-brand"
      >
        {text}
        <Icon name="external" size={11} />
        <span className="sr-only">(opens Blockscout in a new tab)</span>
      </a>
      {copy && (
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => {
              setCopied(true)
              setTimeout(() => setCopied(false), 1400)
            })
          }}
          className="rounded p-0.5 text-ink-3 hover:bg-sunken hover:text-ink"
          aria-label={copied ? 'Copied' : `Copy ${noun}`}
          title={copied ? 'Copied' : 'Copy'}
        >
          <Icon name={copied ? 'check' : 'copy'} size={11} />
        </button>
      )}
    </span>
  )
}

/** A value with a copy button, no link (evidence ids, api keys). */
export function CopyValue({ value, display, className }: { value: string; display?: string; className?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <span className={cx('inline-flex min-w-0 max-w-full items-center gap-1 font-mono text-[12.5px]', className)}>
      <span className="truncate" title={value}>
        {display ?? value}
      </span>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1400)
          })
        }}
        className="shrink-0 rounded p-0.5 text-ink-3 hover:bg-sunken hover:text-ink"
        aria-label={copied ? 'Copied' : 'Copy value'}
      >
        <Icon name={copied ? 'check' : 'copy'} size={12} />
      </button>
    </span>
  )
}
